import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import { flushSync } from 'react-dom'

import { prefersReducedMotion } from '../../ui/motion'

type Theme = 'system' | 'light' | 'dark'
/** Where a theme change was asked for, in viewport pixels: the new theme spreads from here. */
export interface ThemeOrigin { x: number; y: number }
interface ThemeContextValue { theme: Theme; setTheme: (theme: Theme) => void; cycle: (origin?: ThemeOrigin) => void }
const ThemeContext = createContext<ThemeContextValue | null>(null)

const STORAGE_KEY = 'ddws.console.theme'

function readTheme(): Theme {
  const saved = window.localStorage.getItem(STORAGE_KEY)
  return saved === 'light' || saved === 'dark' ? saved : 'system'
}

/** The DOM half of a theme change: the attribute the token sheet keys on, and the remembered choice. */
function applyTheme(theme: Theme) {
  if (theme === 'system') { delete document.documentElement.dataset.theme; window.localStorage.removeItem(STORAGE_KEY) }
  else { document.documentElement.dataset.theme = theme; window.localStorage.setItem(STORAGE_KEY, theme) }
}

function nextTheme(theme: Theme): Theme {
  return theme === 'system' ? 'light' : theme === 'light' ? 'dark' : 'system'
}

type ViewTransitionDocument = Document & {
  startViewTransition?: (update: () => void) => { ready: Promise<void> }
}

/**
 * Change the theme, and — when the caller says where the change came from and
 * the browser can — let the new one spread out from that point.
 *
 * The spread is opt-in by passing an origin. A caller that passes none (the
 * provider panel's toggle, which ships without the stylesheet that turns the
 * browser's own cross-fade off) gets the plain, instant change it always had.
 *
 * The attribute is applied inside the view transition's callback — not left
 * to the effect below — because the transition photographs the page when the
 * callback returns, and an effect runs too late to be in that picture. Without
 * view transitions, or with reduced motion, it is an ordinary state change.
 */
function changeTheme(next: Theme, commit: (theme: Theme) => void, origin?: ThemeOrigin) {
  const doc = document as ViewTransitionDocument
  if (!origin || typeof doc.startViewTransition !== 'function' || prefersReducedMotion()) { commit(next); return }
  const { x, y } = origin
  const radius = Math.hypot(Math.max(x, window.innerWidth - x), Math.max(y, window.innerHeight - y))
  const transition = doc.startViewTransition(() => {
    applyTheme(next)
    flushSync(() => commit(next))
  })
  transition.ready
    .then(() => {
      document.documentElement.animate(
        { clipPath: [`circle(0px at ${x}px ${y}px)`, `circle(${radius}px at ${x}px ${y}px)`] },
        { duration: 640, easing: 'cubic-bezier(.65, 0, .35, 1)', pseudoElement: '::view-transition-new(root)' },
      )
    })
    .catch(() => undefined)
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setThemeState] = useState<Theme>(readTheme)
  const setTheme = (next: Theme) => setThemeState(next)
  useEffect(() => { applyTheme(theme) }, [theme])
  const value = useMemo(() => ({ theme, setTheme, cycle: (origin?: ThemeOrigin) => changeTheme(nextTheme(theme), setThemeState, origin) }), [theme])
  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
}

export function useTheme(): ThemeContextValue {
  const context = useContext(ThemeContext)
  const [fallbackTheme, setFallbackTheme] = useState<Theme>(readTheme)
  useEffect(() => {
    if (context) return
    applyTheme(fallbackTheme)
  }, [context, fallbackTheme])
  const fallback = useMemo(() => ({
    theme: fallbackTheme,
    setTheme: setFallbackTheme,
    cycle: (origin?: ThemeOrigin) => changeTheme(nextTheme(fallbackTheme), setFallbackTheme, origin),
  }), [fallbackTheme])
  return context ?? fallback
}
